import {MinusCircleOutlined, PlusCircleOutlined} from '@ant-design/icons';
import {Form, Input, Tooltip, Badge, Button} from 'antd';
import cn from 'classnames';
import React, {useState, useEffect, ReactElement} from 'react';

import {CustomSelect} from 'components/form/inputs/custom-select';
import {DateCell} from 'components/table/columns/date-cell/date-cell';

import {SUBMIT_GROUP_COMPANY_DRAFT_NAME, HAS_FOREIGN_PARENT_FIELD_VALUES} from '../../../model/constants';
import {IOption, ISubmitGroupCompanyForm} from '../../../model/types';
import {ForeignSubmitGroupCompanyItem} from './foreign-submit-group-company-item';
import styles from './submit-group-company.module.less';

export const SubmitGroupCompanyForm = <T,>({
    fields,
    onFinish,
    formInstance,
}: ISubmitGroupCompanyForm<T>): ReactElement => {
    // Локальное состояние для видимости блока условий (иконка +/– и отображение Form.List)
    const [areConditionsHidden, setAreConditionsHidden] = useState(true);
    // Количество записей в списке иностранных компаний (для бейджа и иконки)
    const [fieldsCount, setFieldsCount] = useState<number>(0);

    const [isEditEnabled, setIsEditEnabled] = useState<boolean>(true);

    // Инициализация формы из пропса fields
    useEffect(() => {
        if (!fields) return;

        const formValues: any = { ...fields };

        if (formValues.foreignParentInfoList === []) {
            formValues.foreignParentInfoList = undefined;
        }

        const list = fields.foreignParentInfoList || [];
        formValues.foreignParentInfoList = list;

        // Определяем видимость блока и количество записей
        const hasForeignParentYes = fields.hasForeignParent === HAS_FOREIGN_PARENT_FIELD_VALUES['есть'];
        if (hasForeignParentYes) {
            if (list.length === 0) {
                formValues.foreignParentInfoList = [{}];
                setFieldsCount(1);
                setAreConditionsHidden(false);  // сразу показываем блок
            } else {
                setFieldsCount(list.length);
                setAreConditionsHidden(false);
            }
        } else {
            setFieldsCount(0);
            setAreConditionsHidden(true);
            formValues.foreignParentInfoList = [];
        }

        formInstance.setFieldsValue(formValues);
    }, [fields, formInstance]);

    // Режим редактирования (только для черновика)
    useEffect(() => {
        if (fields && fields?.status !== SUBMIT_GROUP_COMPANY_DRAFT_NAME) {
            setIsEditEnabled(false);
        } else {
            setIsEditEnabled(true);
        }
    }, [fields]);

    // Обработчик изменения селекта "Признак наличия..."
    const handleHasForeignParentChange = (_: any, option: IOption) => {
        const isYes = option.key === HAS_FOREIGN_PARENT_FIELD_VALUES['есть'];
        if (!isYes) {
            // При выборе "нет" – очищаем список и прячем блок
            formInstance.setFieldsValue({ foreignParentInfoList: [] });
            setFieldsCount(0);
            setAreConditionsHidden(true);
        } else {
            // При выборе "есть" – если список пуст, добавляем пустую запись
            const currentList = formInstance.getFieldValue('foreignParentInfoList') || [];
            if (currentList.length === 0) {
                formInstance.setFieldsValue({ foreignParentInfoList: [{}] });
                setFieldsCount(1);
            } else {
                setFieldsCount(currentList.length);
            }
            setAreConditionsHidden(false);
        }
    };

    // Рендер иконки-переключателя блока с иностранными компаниями
    const conditionsIcon = () => {
        if (!fieldsCount) {
            return (
                <Tooltip title="Нет добавленных сведений" mouseEnterDelay={0.8}>
                    <MinusCircleOutlined className={styles['submit-group-company-form_icon_default']} />
                </Tooltip>
            );
        }
        if (areConditionsHidden) {
            return (
                <Tooltip title="Показать условия" mouseEnterDelay={0.8}>
                    <PlusCircleOutlined
                        className={styles['submit-group-company-form_icon_active']}
                        onClick={() => setAreConditionsHidden(false)}
                    />
                </Tooltip>
            );
        }
        return (
            <Tooltip title="Скрыть условия" mouseEnterDelay={0.8}>
                <MinusCircleOutlined
                    className={styles['submit-group-company-form_icon_active']}
                    onClick={() => setAreConditionsHidden(true)}
                />
            </Tooltip>
        );
    };

    const handleFinish = (values: T) => {
        onFinish?.(values);
        setAreConditionsHidden(true);
        setFieldsCount(0);
    }

    return (
        <Form layout="vertical" form={formInstance} onFinish={handleFinish}>
            {/* ========== Блок 1: Основные сведения ========== */}
            <section>
                <h4 className={styles['submit-group-company-form__title']}>Основные сведения</h4>
                <div className="d-flex gap-2">
                    <Form.Item
                        name="reportingDate"
                        label="Дата, по состоянию на которую предоставляются сведения"
                        rules={[{ required: true }]}
                        validateTrigger="onBlur"
                        className={styles['submit-group-company-form__half-width']}
                    >
                        <DateCell format="DD.MM.YYYY" disabled={!isEditEnabled} />
                    </Form.Item>
                    <Form.Item
                        name="requestId"
                        label="Номер запроса"
                        className={styles['submit-group-company-form__half-width']}
                    >
                        <CustomSelect
                            settings={{
                                url: 'valueLists/FLEX_VALUE?sublistCode=state_part',
                                valuePath: 'id',
                                labelPath: 'description',
                                isDisabled: true,
                            }}
                        />
                    </Form.Item>
                </div>
                <div className="d-flex gap-2">
                    <Form.Item name="requestDate" label="Дата отправки запроса" className={styles['submit-group-company-form__half-width']}>
                        <DateCell disabled format="DD.MM.YYYY" />
                    </Form.Item>
                    <Form.Item name="status" label="Статус" className={styles['submit-group-company-form__half-width']}>
                        <CustomSelect
                            settings={{
                                url: 'valueLists/FLEX_VALUE?sublistCode=state_part',
                                valuePath: 'id',
                                labelPath: 'description',
                                isDisabled: true,
                            }}
                        />
                    </Form.Item>
                </div>
            </section>

            {/* ========== Блок 2: Сведения о группе компаний ========== */}
            <section>
                <h4 className={styles['submit-group-company-form__title']}>
                    Сведения о группе компаний, в составе которой учтена организация, по которой представлены сведения
                </h4>
                <div className="d-flex">
                    {/* Иконка-переключатель – класс для вертикальной линии берётся из рабочего варианта */}
                    <Form.Item
                        label={<>&nbsp;</>}
                        className={cn(styles['submit-group-company-form__tooltip'], {
                            [styles['submit-group-company-form__decor-vertical-line']]: !areConditionsHidden,
                        })}
                    >
                        {conditionsIcon()}
                    </Form.Item>
                    <div>
                        <div className="d-flex gap-2">
                            <Form.Item
                                name="groupCode"
                                label="Код группы компаний (холдинга)"
                                rules={[
                                    { required: true },
                                    { pattern: /^\d{1,3}$/, message: 'Количество цифр должно быть от 1 до 3' },
                                ]}
                                validateTrigger="onBlur"
                                className={styles['submit-group-company-form__half-width']}
                            >
                                <Input
                                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                                    disabled={!isEditEnabled}
                                />
                            </Form.Item>
                            <Form.Item
                                name="groupName"
                                label="Наименование группы компаний (холдинга)"
                                rules={[
                                    { required: true },
                                    { pattern: /^.{1,250}$/, message: 'Количество символов должно быть не более 250' },
                                ]}
                                validateTrigger="onBlur"
                                className={styles['submit-group-company-form__half-width']}
                            >
                                <Input disabled={!isEditEnabled} />
                            </Form.Item>
                        </div>
                        <div className="d-flex gap-2 align-items-end">
                            <Form.Item
                                name="headInn"
                                label="ИНН российской головной организации группы компаний (холдинга)"
                                rules={[{ pattern: /^\d{10}$/, message: 'Количество цифр должно быть равное 10' }]}
                                validateTrigger="onBlur"
                                className={styles['submit-group-company-form__half-width']}
                            >
                                <Input
                                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                                    disabled={!isEditEnabled}
                                />
                            </Form.Item>
                            <Form.Item
                                name="ogrn"
                                label="ОГРН"
                                rules={[{ pattern: /^\d{13}$/, message: 'Количество цифр должно быть равное 13' }]}
                                validateTrigger="onBlur"
                                className={styles['submit-group-company-form__half-width']}
                            >
                                <Input
                                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                                    disabled={!isEditEnabled}
                                />
                            </Form.Item>
                        </div>
                        <div className="d-flex gap-2 align-items-end">
                            <Form.Item
                                name="headName"
                                label="Наименование российской головной организации группы компаний (холдинга)"
                                className="w-100"
                                rules={[{ pattern: /^.{1,250}$/, message: 'Количество символов должно быть не более 250' }]}
                                validateTrigger="onBlur"
                            >
                                <Input disabled={!isEditEnabled} />
                            </Form.Item>
                            <Form.Item
                                name="hasForeignParent"
                                label="Признак наличия иностранной материнской компании у группы компаний или российской головной организации группы компаний (холдинга)"
                                className={styles['submit-group-company-form__has-foreign-parent']}
                                rules={[{ required: true }]}
                            >
                                <CustomSelect
                                    onChange={handleHasForeignParentChange}
                                    settings={{
                                        url: 'valueLists/FLEX_VALUE?sublistCode=has_foreign_parent',
                                        valuePath: 'lookupCode',
                                        labelPath: 'description',
                                        isDisabled: !isEditEnabled,
                                    }}
                                />
                            </Form.Item>
                            {/* Кнопка добавления новой записи об иностранной компании */}
                            <Form.Item label={<>&nbsp;</>}>
                                <Tooltip title="Добавить сведения" placement="top" mouseEnterDelay={0.8}>
                                    <Badge count={fieldsCount} size="small">
                                        <Button
                                            disabled={areConditionsHidden || !isEditEnabled}
                                            type="primary"
                                            className="btn-only-icon"
                                            onClick={() => {
                                                const currentList = formInstance.getFieldValue('foreignParentInfoList') || [];
                                                formInstance.setFieldsValue({
                                                    foreignParentInfoList: [...currentList, {}],
                                                });
                                                setFieldsCount(currentList.length + 1);
                                                // Если блок был скрыт – показываем
                                                if (areConditionsHidden) setAreConditionsHidden(false);
                                            }}
                                        >
                                            <PlusCircleOutlined />
                                        </Button>
                                    </Badge>
                                </Tooltip>
                            </Form.Item>
                        </div>

                        {/* Блок динамического списка иностранных компаний */}
                        {!areConditionsHidden && (
                            <div className="submit-group-company-form__bottom">
                                <div className={styles['submit-group-company-form__bottom--line']} />
                                <div>
                                    <Form.List name="foreignParentInfoList">
                                        {(conditionsFields, conditionsListOperations) => (
                                            <ForeignSubmitGroupCompanyItem
                                                conditionsFields={conditionsFields}
                                                conditionsListOperations={conditionsListOperations}
                                                areFieldsDisabled={!isEditEnabled}
                                            />
                                        )}
                                    </Form.List>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ========== Блок 3: Результаты рассмотрения ========== */}
            {(!!fields?.responseId || fields?.responseDate || fields?.result || fields?.comment) && (
                <section>
                    <h4 className={styles['submit-group-company-form__title']}>Результаты рассмотрения запроса</h4>
                    <div className="d-flex gap-2">
                        <Form.Item name="responseId" label="Номер ответа" className={styles['submit-group-company-form__half-width']}>
                            <CustomSelect
                                settings={{
                                    url: 'valueLists/FLEX_VALUE?sublistCode=state_part',
                                    valuePath: 'id',
                                    labelPath: 'description',
                                    isDisabled: true,
                                }}
                            />
                        </Form.Item>
                        <Form.Item name="responseDate" label="Дата ответа" className={styles['submit-group-company-form__half-width']}>
                            <DateCell disabled format="DD.MM.YYYY" />
                        </Form.Item>
                    </div>
                    <Form.Item name="result" label="Результат рассмотрения" className={styles['submit-group-company-form__half-width']}>
                        <CustomSelect
                            settings={{
                                url: 'valueLists/FLEX_VALUE?sublistCode=state_part',
                                valuePath: 'id',
                                labelPath: 'description',
                                isDisabled: true,
                            }}
                        />
                    </Form.Item>
                    <Form.Item name="resultComment" label="Комментарий">
                        <Input disabled />
                    </Form.Item>
                </section>
            )}

            <Form.Item name="id" hidden />
        </Form>
    );
};
